import { makeAuthenticatedRequest } from "./authApi";
import { getApiUrl } from "../utils/envConfig";
import type { components } from "./generated/schema";

// Request/response contracts sourced from the FastAPI OpenAPI schema via
// openapi-typescript (npm run gen:api-types). These types are generated
// from the backend Pydantic models, so they can't drift from what the API
// actually accepts and returns — CI fails the build if schema.ts is stale.
type BrandTreeResponse = components["schemas"]["BrandTreeResponse"];
type CompanyBrandTreeResponse =
  components["schemas"]["CompanyBrandTreeResponse"];
type CreatePetFormulaRequest =
  components["schemas"]["CreatePetFormulaRequest"];
type AllUsersResponse = components["schemas"]["AllUsersResponse"];
type CreateUserRequest = components["schemas"]["CreateUserRequest"];
type UserResponse = components["schemas"]["UserResponse"];
type CreateUserResponse = components["schemas"]["CreateUserResponse"];

/**
 * Map the legacy two-lens market selections (Market Lens / Execution Lens) onto
 * the 5 canonical per-module product_scopes keys the backend now requires.
 * Mirror of dashone-admin's internal_tool_endpoints.js helper.
 *
 * - market_overview / market_deepdive  ← Market Lens markets
 * - execution_view                      ← Execution Lens markets
 * - physical_location / physical_franchise ← union of both lenses
 */
export const buildPerModuleProductScopes = (
  marketLens: string[] = [],
  executionLens: string[] = []
) => {
  const market = marketLens || [];
  const execution = executionLens || [];
  const union = [...new Set([...market, ...execution])];
  return {
    market_overview: market,
    market_deepdive: market,
    execution_view: execution,
    physical_location: union,
    physical_franchise: union,
  };
};

export const fetchAllIdClients = async (): Promise<unknown> => {
  try {
    const url = `${getApiUrl()}/distinct_id_client`;
    const idClientsData = await makeAuthenticatedRequest(url);
    return idClientsData;
  } catch (error) {
    throw new Error("Failing fetching all client ids: " + error.message);
  }
};

export const fetchApiClients = async (): Promise<unknown> => {
  try {
    const url = `${getApiUrl()}/distinct_api_client`;
    const data = await makeAuthenticatedRequest(url);
    return data;
  } catch (error) {
    throw new Error("Failed to fetch API clients: " + error.message);
  }
};

export const fetchAllMarkets = async ({
  id_client,
}: {
  id_client: string;
}): Promise<string[]> => {
  try {
    const url = `${getApiUrl()}/client/${id_client}/countries`;
    const marketsData = await makeAuthenticatedRequest(url);
    return marketsData;
  } catch (error) {
    throw new Error("Failing fetching all markets: " + error.message);
  }
};

export const fetchRefresh = async ({
  id_client,
}: {
  id_client: string;
}): Promise<string[]> => {
  try {
    const url = `${getApiUrl()}/client/${id_client}/refresh`;
    const refreshData = await makeAuthenticatedRequest(url);
    return refreshData;
  } catch (error) {
    throw new Error("Failing fetching refresh data: " + error.message);
  }
};

export const fetchSKUCompanies = async ({
  id_client,
  country,
}: {
  id_client: string;
  country: string;
}): Promise<string[]> => {
  try {
    const url = `${getApiUrl()}/client/${id_client}/country/${country}/brands/companies`;
    const companiesData = await makeAuthenticatedRequest(url);
    return companiesData;
  } catch (error) {
    throw new Error(
      "Failing fetching internal tool companies: " + error.message
    );
  }
};

export const fetchCompanyAllBrands = async ({
  id_client,
  country,
  company,
}: {
  id_client: string;
  country: string;
  company: string;
}): Promise<BrandTreeResponse> => {
  try {
    const url = `${getApiUrl()}/client/${id_client}/country/${country}/brands/tree/${encodeURIComponent(company)}`;
    const brandsData = await makeAuthenticatedRequest(url);
    return brandsData;
  } catch (error) {
    throw new Error("Failing fetching internal tool brands: " + error.message);
  }
};

export const fetchCompaniesWithBrands = async ({
  id_client,
  country,
}: {
  id_client: string;
  country: string;
}): Promise<CompanyBrandTreeResponse> => {
  try {
    const url = `${getApiUrl()}/client/${id_client}/country/${country}/brands/tree`;
    const companiesBrandsData = await makeAuthenticatedRequest(url);
    return companiesBrandsData;
  } catch (error) {
    console.error(`🌐 fetchCompaniesWithBrands API error:`, error);
    throw new Error(
      "Failing fetching internal tool companies with brands: " + error.message
    );
  }
};
export const createWorkspace = async ({
  id_client,
  workspace_info,
}: {
  id_client: string;
  workspace_info: CreatePetFormulaRequest;
}): Promise<Record<string, string>> => {
  try {
    const url = `${getApiUrl()}/${id_client}/pet_formula/default`;
    const createWorkspaceData = await makeAuthenticatedRequest(url, {
      method: "POST",
      body: JSON.stringify(workspace_info),
    });

    return createWorkspaceData;
  } catch (error) {
    console.error(`🌐 createWorkspace API error:`, error);
    throw new Error("Failing createWorkspace: " + error.message);
  }
};

export const getAllWorkspaces = async (idClient: string) => {
  try {
    const url = `${getApiUrl()}/${idClient}/formula_weights`;
    const response = await makeAuthenticatedRequest(url);

    // The API returns { data: [...], total_clients: number, total_records: number }
    return {
      workspaces: response.data || [],
      totalClients: response.total_clients || 0,
      totalRecords: response.total_records || 0,
      raw: response,
    };
  } catch (error) {
    console.error(`🌐 getAllWorkspaces API error:`, error);
    throw new Error("Failing getAllWorkspaces: " + error.message);
  }
};

export const createNewRegion = async ({
  id_client,
  regions_info,
}: {
  id_client: string;
  regions_info: unknown;
}): Promise<unknown> => {
  try {
    const url = `${getApiUrl()}/${id_client}/geoscope/`;
    const newRegionData = await makeAuthenticatedRequest(url, {
      method: "POST",
      body: JSON.stringify(regions_info),
    });

    return newRegionData;
  } catch (error) {
    console.error(`🌐 Create new region API error:`, error);
    throw new Error("Failing createNewRegion: " + error.message);
  }
};

export const inviteUsersToWorkspaces = async ({
  emails,
  workspaceAssignments,
  sendEmail = true,
  domainOverrideReason,
}: {
  emails: Array<{ email: string; name?: string }>;
  workspaceAssignments: Array<Record<string, any>>;
  sendEmail?: boolean;
  // Dashmote-admin justification for inviting an address outside the
  // workspace's allowed domains. Ignored server-side for non-admin callers.
  domainOverrideReason?: string;
}) => {
  try {
    const url = `${getApiUrl()}/users/invite`;
    const results = [];

    // Process each email separately with multiple API calls
    for (const emailObj of emails) {
      // Get the first workspace assignment's ID as default_id_client
      const defaultIdClient =
        workspaceAssignments.length > 0
          ? workspaceAssignments[0].workspaceId
          : null;

      const userData: any = {
        email: emailObj.email,
        name: emailObj.name && emailObj.name.trim() !== "" ? emailObj.name.trim() : null,
        default_id_client: defaultIdClient,
        id_client: {},
        send_email: sendEmail,
      };

      if (domainOverrideReason) {
        userData.domain_override_reason = domainOverrideReason;
      }

      // Process each workspace assignment for this user
      workspaceAssignments.forEach((assignment) => {
        const workspaceId = assignment.workspaceId;
        const role = assignment.role;

        // Per-module country scopes, keyed by the canonical product keys:
        // market_overview, market_deepdive, execution_view,
        // physical_location, physical_franchise.
        const productScopes = assignment.productScopes || {};

        if (role === "super_admin") {
          // Super Admin structure - no product_scopes needed
          userData.id_client[workspaceId] = {
            role: "super_admin",
            // is_super_admin will be set automatically to true by backend
          };
        } else if (role === "admin") {
          // Admin structure - product_scopes required
          userData.id_client[workspaceId] = {
            role: "admin",
            product_scopes: { ...productScopes },
          };
        } else {
          // Normal User structure - product_scopes required
          userData.id_client[workspaceId] = {
            role: "normal_user",
            // is_super_admin will be set automatically to false by backend
            product_scopes: { ...productScopes },
          };
        }
      });

      // Make individual API call for each user
      const result = await makeAuthenticatedRequest(url, {
        method: "POST",
        body: JSON.stringify(userData),
      });

      results.push({
        email: emailObj.email,
        success: true,
        result: result,
      });
    }

    return results;
  } catch (error) {
    console.error(`🌐 Invite users API error:`, error);
    throw error;
  }
};

export const getAllUsersInfo = async (): Promise<AllUsersResponse> => {
  try {
    const url = `${getApiUrl()}/all-users`;
    const allUsersData = await makeAuthenticatedRequest(url);
    return allUsersData;
  } catch (error) {
    throw new Error("Failing fetching all users: " + error.message);
  }
};

export const updateUserInfo = async ({
  email,
  user_info,
}: {
  email: string;
  user_info: CreateUserRequest;
}): Promise<UserResponse> => {
  try {
    const url = `${getApiUrl()}/users/email/${email}`;
    const updateUserInfoData = await makeAuthenticatedRequest(url, {
      method: "PUT",
      body: JSON.stringify(user_info),
    });

    return updateUserInfoData;
  } catch (error) {
    console.error(`🌐 Update User Info API error:`, error);
    throw new Error("Failing update user info: " + error.message);
  }
};

export const createUserByEmail = async ({
  email,
  name,
  workspaceAssignments,
}: {
  email: string;
  name: string;
  workspaceAssignments: Array<Record<string, any>>;
}): Promise<CreateUserResponse> => {
  try {
    const url = `${getApiUrl()}/users/email`;

    // Get the first workspace assignment's ID as default_id_client
    const defaultIdClient =
      workspaceAssignments.length > 0 ? workspaceAssignments[0].workspaceId : null;

    const userData: any = {
      email: email,
      name: name,
      default_id_client: defaultIdClient,
      id_client: {},
    };

    // Process each workspace assignment
    workspaceAssignments.forEach((assignment) => {
      const workspaceId = assignment.workspaceId;
      const role = assignment.role;

      if (role === "super_admin") {
        userData.id_client[workspaceId] = {
          role: "super_admin",
        };
      } else if (role === "admin") {
        userData.id_client[workspaceId] = {
          role: "admin",
          product_scopes: buildPerModuleProductScopes(
            assignment.marketLens,
            assignment.executionLens
          ),
        };
      } else {
        userData.id_client[workspaceId] = {
          role: "normal_user",
          product_scopes: buildPerModuleProductScopes(
            assignment.marketLens,
            assignment.executionLens
          ),
        };
      }
    });

    const result = await makeAuthenticatedRequest(url, {
      method: "POST",
      body: JSON.stringify(userData),
    });

    return result;
  } catch (error) {
    console.error(`🌐 Create user by email API error:`, error);
    throw new Error("Failing createUserByEmail: " + error.message);
  }
};
